import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  listMuteCandidates,
  muteDisplayName,
  muteTarget,
  muteTargetId,
  unmuteTarget,
  type MuteCandidateUser,
  type MuteDuration,
  type MuteSource,
  type MuteView,
} from "@/api/mutes";
import { extractApiError } from "@/api/client";
import { mutesQuery } from "@/lib/queries";
import { remainingLabel } from "@/lib/format";
import {
  Avatar,
  Card,
  Chip,
  EmptyCard,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  RadioRow,
  SectionLabel,
} from "@/components/ui";
import { Modal } from "@/components/Modal";

/**
 * Mutes — mirrors iOS MutesScreen. A viewer-side privacy filter:
 * lists current mutes with remaining-time badges, unmute, and an
 * add-mute picker (people / linked circles) with the three-duration
 * drawer. Copy verbatim from iOS.
 */

const MutesPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <MutesView />
    </AppShell>
  );
};

const MutesView = () => {
  const queryClient = useQueryClient();
  const mutes = useQuery(mutesQuery);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: ["mutes"] });

  const unmute = useMutation({
    mutationFn: (mute: MuteView) => unmuteTarget(muteTargetId(mute)),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't unmute right now. Please try again.",
      ),
  });

  if (mutes.isPending) return <LoadingBlock label="Loading your mutes…" />;

  const userMutes = (mutes.data ?? []).filter((m) => m.kind === "user");
  const familyMutes = (mutes.data ?? []).filter((m) => m.kind === "family");

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <h1
          className="font-serif text-[28px] leading-[34px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Mutes
        </h1>
        <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
          A private lens. No one sees who you've muted.
        </p>
      </header>

      {mutes.isError && (
        <div className="mt-8">
          <EmptyCard
            eyebrow="Couldn't load"
            blurb="We couldn't load your mutes. Please try again."
          />
        </div>
      )}

      {!mutes.isError && userMutes.length === 0 && familyMutes.length === 0 && (
        <div className="mt-8">
          <EmptyCard blurb="Nothing muted right now. Mute someone when their posts are more than you can hold." />
        </div>
      )}

      {userMutes.length > 0 && (
        <section className="mt-8">
          <SectionLabel>People · {userMutes.length}</SectionLabel>
          <MuteList
            mutes={userMutes}
            onUnmute={(m) => unmute.mutate(m)}
            busy={unmute.isPending}
          />
        </section>
      )}

      {familyMutes.length > 0 && (
        <section className="mt-8">
          <SectionLabel>Linked circles · {familyMutes.length}</SectionLabel>
          <MuteList
            mutes={familyMutes}
            onUnmute={(m) => unmute.mutate(m)}
            busy={unmute.isPending}
          />
        </section>
      )}

      <ErrorText>{error}</ErrorText>

      <div className="mt-8">
        <InkButton onClick={() => setPickerOpen(true)}>Add a mute</InkButton>
      </div>

      {pickerOpen && (
        <AddMuteDialog
          onClose={() => setPickerOpen(false)}
          onMuted={() => {
            setPickerOpen(false);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const MuteList = ({
  mutes,
  onUnmute,
  busy,
}: {
  mutes: MuteView[];
  onUnmute: (m: MuteView) => void;
  busy: boolean;
}) => (
  <Card>
    {mutes.map((m, i) => {
      const name = muteDisplayName(m);
      return (
        <div key={`${m.kind}-${muteTargetId(m)}`}>
          {i > 0 && <Hairline />}
          <div className="flex items-center gap-4 px-5 py-4">
            <Avatar
              name={name}
              url={
                m.kind === "user"
                  ? m.target_user?.avatar_url
                  : m.target_family?.family_avatar_url
              }
              size={40}
            />
            <div className="min-w-0 flex-1">
              <p className="truncate font-sans text-[15px] font-medium text-ink">
                {name}
              </p>
              {m.kind === "family" &&
                m.target_family?.owner_display_name && (
                  <p className="font-sans text-[13px] text-ink-soft">
                    Owned by {m.target_family.owner_display_name}
                  </p>
                )}
            </div>
            <Chip>{remainingLabel(m.expires_at)}</Chip>
            <QuietButton
              onClick={() => onUnmute(m)}
              disabled={busy}
              ariaLabel={`Unmute ${name}`}
            >
              Unmute
            </QuietButton>
          </div>
        </div>
      );
    })}
  </Card>
);

// ── Add-a-mute picker + duration drawer ─────────────────────────────

type PickedTarget =
  | { kind: "user"; id: string; name: string; sub: string; source: MuteSource }
  | { kind: "family"; id: string; name: string; sub: string };

const AddMuteDialog = ({
  onClose,
  onMuted,
}: {
  onClose: () => void;
  onMuted: () => void;
}) => {
  const [scope, setScope] = useState<"people" | "families">("people");
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<PickedTarget | null>(null);
  const [error, setError] = useState<string | null>(null);

  const candidates = useQuery({
    queryKey: ["mute-candidates"] as const,
    queryFn: listMuteCandidates,
    staleTime: 15_000,
  });

  // People = home-circle members + every linked family's members,
  // deduplicated by user id (circle source wins), filtered locally.
  const people = useMemo(() => {
    if (!candidates.data) return [];
    const seen = new Set<string>();
    const rows: Array<{
      user: MuteCandidateUser;
      sub: string;
      source: MuteSource;
    }> = [];
    for (const u of candidates.data.circle_members) {
      seen.add(u.user_id);
      rows.push({ user: u, sub: "Your circle", source: "circle" });
    }
    for (const fam of candidates.data.linked_circles) {
      for (const u of fam.members) {
        if (seen.has(u.user_id)) continue;
        seen.add(u.user_id);
        rows.push({ user: u, sub: fam.family_name, source: "linked" });
      }
    }
    const q = query.trim().toLowerCase();
    return q
      ? rows.filter((r) => r.user.display_name.toLowerCase().includes(q))
      : rows;
  }, [candidates.data, query]);

  const familyRows = useMemo(() => {
    if (!candidates.data) return [];
    const q = query.trim().toLowerCase();
    return q
      ? candidates.data.linked_circles.filter((f) =>
          f.family_name.toLowerCase().includes(q),
        )
      : candidates.data.linked_circles;
  }, [candidates.data, query]);

  const mute = useMutation({
    mutationFn: ({
      target,
      duration,
    }: {
      target: PickedTarget;
      duration: MuteDuration;
    }) =>
      muteTarget(
        target.id,
        duration,
        target.kind === "user" ? target.source : "linked",
      ),
    onSuccess: onMuted,
    onError: (err) => {
      setPicked(null);
      setError(
        extractApiError(err)?.message ??
          "Couldn't mute right now. Please try again.",
      );
    },
  });

  if (picked) {
    return (
      <DurationDialog
        target={picked}
        busy={mute.isPending}
        onConfirm={(duration) => mute.mutate({ target: picked, duration })}
        onBack={() => setPicked(null)}
      />
    );
  }

  return (
    <Modal title="Mute someone" onClose={onClose} wide>
      <div
        role="tablist"
        aria-label="Who to mute"
        className="flex gap-2"
      >
        {(
          [
            ["people", `People · ${people.length}`],
            ["families", `Families · ${familyRows.length}`],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={scope === key}
            onClick={() => setScope(key)}
            className={`tap-target rounded-full px-4 py-2 font-mono text-[12px] uppercase tracking-[0.14em] transition-colors ${
              scope === key
                ? "bg-taupe/25 text-ink"
                : "text-taupe-deep hover:bg-paper"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search people…"
        aria-label="Search people"
        className="tap-target mt-4 w-full rounded-full border border-sand bg-paper px-5 py-2.5 font-sans text-[15px] outline-none placeholder:text-ink-soft/60 focus:border-taupe"
      />

      <div className="mt-4">
        {candidates.isPending ? (
          <LoadingBlock label="Finding who you can mute…" />
        ) : candidates.isError ? (
          <ErrorText>
            Couldn't load who you can mute. Please try again.
          </ErrorText>
        ) : scope === "people" ? (
          people.length === 0 ? (
            <p className="py-6 text-center font-serif text-[14px] italic text-ink-soft">
              {query
                ? `No one matches "${query}".`
                : "No one to mute right now."}
            </p>
          ) : (
            <Card className="border border-sand/40">
              {people.map((row, i) => (
                <div key={row.user.user_id}>
                  {i > 0 && <Hairline />}
                  <CandidateRow
                    name={row.user.display_name}
                    avatarUrl={row.user.avatar_url}
                    sub={row.sub}
                    alreadyMuted={!!row.user.current_mute}
                    onPick={() =>
                      setPicked({
                        kind: "user",
                        id: row.user.user_id,
                        name: row.user.display_name,
                        sub: row.sub,
                        source: row.source,
                      })
                    }
                  />
                </div>
              ))}
            </Card>
          )
        ) : familyRows.length === 0 ? (
          <p className="py-6 text-center font-serif text-[14px] italic text-ink-soft">
            {query
              ? `No one matches "${query}".`
              : "No linked circles to mute right now."}
          </p>
        ) : (
          <Card className="border border-sand/40">
            {familyRows.map((fam, i) => (
              <div key={fam.family_id}>
                {i > 0 && <Hairline />}
                <CandidateRow
                  name={`Mute the ${fam.family_name} circle`}
                  avatarUrl={fam.family_avatar_url}
                  avatarName={fam.family_name}
                  sub={
                    fam.owner_display_name
                      ? `Owned by ${fam.owner_display_name}`
                      : ""
                  }
                  alreadyMuted={!!fam.current_mute}
                  onPick={() =>
                    setPicked({
                      kind: "family",
                      id: fam.family_id,
                      name: fam.family_name,
                      sub: fam.owner_display_name
                        ? `Owned by ${fam.owner_display_name}`
                        : "",
                    })
                  }
                />
              </div>
            ))}
          </Card>
        )}
      </div>

      <ErrorText>{error}</ErrorText>
    </Modal>
  );
};

const CandidateRow = ({
  name,
  avatarUrl,
  avatarName,
  sub,
  alreadyMuted,
  onPick,
}: {
  name: string;
  avatarUrl: string | null;
  avatarName?: string;
  sub: string;
  alreadyMuted: boolean;
  onPick: () => void;
}) => (
  <button
    type="button"
    onClick={onPick}
    disabled={alreadyMuted}
    className="tap-target flex w-full items-center gap-4 px-5 py-3.5 text-left transition-colors hover:bg-paper/60 disabled:cursor-default disabled:hover:bg-transparent"
  >
    <Avatar name={avatarName ?? name} url={avatarUrl} size={38} />
    <span className="min-w-0 flex-1">
      <span className="block truncate font-sans text-[15px] font-medium text-ink">
        {name}
      </span>
      {sub && (
        <span className="block font-sans text-[13px] text-ink-soft">
          {sub}
        </span>
      )}
    </span>
    {alreadyMuted && <Chip>Already muted</Chip>}
  </button>
);

const DurationDialog = ({
  target,
  busy,
  onConfirm,
  onBack,
}: {
  target: PickedTarget;
  busy: boolean;
  onConfirm: (duration: MuteDuration) => void;
  onBack: () => void;
}) => {
  const [duration, setDuration] = useState<MuteDuration>("until_canceled");
  const firstName = target.name.split(" ")[0] ?? target.name;
  const label =
    target.kind === "family" ? `the ${target.name} circle` : firstName;

  return (
    <Modal title={`Mute ${target.name}`} onClose={onBack}>
      {target.sub && (
        <p className="font-sans text-[13px] text-ink-soft">{target.sub}</p>
      )}
      <div className="mt-4">
        <SectionLabel>How long?</SectionLabel>
        <Card className="overflow-hidden border border-sand/40">
          <div role="radiogroup" aria-label="How long?">
            <RadioRow
              label="Until I unmute"
              sub="Most common. You can always unmute later."
              selected={duration === "until_canceled"}
              onSelect={() => setDuration("until_canceled")}
            />
            <Hairline />
            <RadioRow
              label="7 days"
              sub="Auto-unmutes a week from today."
              selected={duration === "7_days"}
              onSelect={() => setDuration("7_days")}
            />
            <Hairline />
            <RadioRow
              label="30 days"
              sub="Auto-unmutes a month from today."
              selected={duration === "30_days"}
              onSelect={() => setDuration("30_days")}
            />
          </div>
        </Card>
      </div>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onBack}>Back</QuietButton>
        <InkButton onClick={() => onConfirm(duration)} disabled={busy}>
          {busy ? "Muting…" : `Mute ${label}`}
        </InkButton>
      </div>
    </Modal>
  );
};

export const Route = createFileRoute("/mutes")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: MutesPage,
});
